import { Injectable } from '@angular/core';
import EthereumProvider from '@walletconnect/ethereum-provider';
import { AppConfig, ConfigService } from 'app';
import { BrowserProvider, Contract, formatUnits, parseUnits } from 'ethers';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { Logger } from 'shared';

// USDT ABI for transfer function
const USDT_ABI = [
  'function transfer(address to, uint256 amount) returns (bool)',
  'function balanceOf(address account) view returns (uint256)',
  'function decimals() view returns (uint8)'
];

// Extend Window interface for ethereum provider
declare global {
  interface Window {
    ethereum?: any;
  }
}

export interface Web3ConnectionState {
  connected: boolean;
  address: string | null;
  chainId: number | null;
  balance: string | null;
  walletConnectUri?: string | null; // QR code URI for mobile
}

@Injectable({
  providedIn: 'root'
})
export class Web3Service {
  protected logger = new Logger('Web3Service');
  private provider: BrowserProvider | null = null;
  private walletConnectProvider: EthereumProvider | null = null;
  private connectionStateSubject = new BehaviorSubject<Web3ConnectionState>({
    connected: false,
    address: null,
    chainId: null,
    balance: null,
    walletConnectUri: null
  });
  private configPromise: Promise<AppConfig | null>;

  public connectionState$ = this.connectionStateSubject.asObservable();

  constructor(private configService: ConfigService) {
    // Load configuration first
    // this.initializeConfig();
    
    this.configPromise = firstValueFrom(this.configService.getConfig());

    // Setup ethereum provider event listeners if available
    this.setupProviderListeners();
  }

  // private async initializeConfig(): Promise<void> {
  //   try {
  //     this.config = firstValueFrom(this.configService.getConfig());
  //     console.log('🔷 Web3 config loaded:', this.config);
  //   } catch (error) {
  //     console.error('❌ Failed to load Web3 config:', error);
  //   }
  // }

  private async getConfig(): Promise<AppConfig | null> {
    return await this.configPromise;
  }

  private updateConnectionState(state: Partial<Web3ConnectionState>): void {
    const currentState = this.connectionStateSubject.value;
    this.connectionStateSubject.next({ ...currentState, ...state });
  }

  private setupProviderListeners(): void {
    if (typeof window !== 'undefined' && window.ethereum) {
      // Listen for account changes
      window.ethereum.on('accountsChanged', (accounts: string[]) => {
        if (accounts.length === 0) {
          this.handleDisconnect();
        } else {
          this.handleAccountChange(accounts[0]);
        }
      });

      // Listen for chain changes - update state instead of reloading
      // This prevents NETWORK_ERROR when user switches networks
      window.ethereum.on('chainChanged', async (chainIdHex: string) => {
        this.logger.log('🔷 Chain changed to:', chainIdHex);
        const chainId = parseInt(chainIdHex, 16);

        // Update connection state with new chain
        const currentState = this.connectionStateSubject.value;
        if (currentState.connected && currentState.address) {
          // Re-initialize provider with new network
          this.provider = new BrowserProvider(window.ethereum);
          const balance = await this.getUsdtBalance(currentState.address);

          this.updateConnectionState({
            chainId: chainId,
            balance: balance
          });

          this.logger.log('🔷 Updated state after chain change:', { chainId, balance });
        }
      });

      // Listen for disconnect
      window.ethereum.on('disconnect', () => {
        this.handleDisconnect();
      });
    }
  }

  private async handleAccountChange(address: string): Promise<void> {
    if (this.provider) {
      const network = await this.provider.getNetwork();
      const chainId = Number(network.chainId);
      // Pass chainId directly since we're updating state immediately after
      const balance = await this.getUsdtBalance(address, chainId);
      this.updateConnectionState({
        connected: true,
        address,
        chainId,
        balance
      });
    }
  }

  private handleDisconnect(): void {
    this.updateConnectionState({
      connected: false,
      address: null,
      chainId: null,
      balance: null,
      walletConnectUri: null
    });
    this.provider = null;
  }

  /**
   * Check if browser has ethereum provider (MetaMask, etc.)
   */
  isWeb3Available(): boolean {
    return typeof window !== 'undefined' && !!window.ethereum;
  }

  /**
   * Connect to browser wallet (MetaMask, etc.)
   * Automatically switches to BSC if connected to wrong network
   */
  async connectWallet(): Promise<Web3ConnectionState> {
    this.logger.log('🔷 connectWallet - isWeb3Available:', this.isWeb3Available());

    if (!this.isWeb3Available()) {
      // If no browser extension, use WalletConnect
      this.logger.log('🔷 No browser wallet detected, using WalletConnect');
      return this.connectWalletConnect();
    }

    const config = await this.getConfig();
    if (!config) {
      throw new Error('Web3 configuration not loaded');
    }

    this.logger.log('🔷 Browser wallet detected, attempting browser connection');
    try {
      // Request account access
      const accounts = await window.ethereum.request({
        method: 'eth_requestAccounts'
      });

      if (!accounts || accounts.length === 0) {
        throw new Error('No accounts found');
      }

      // Create provider
      this.provider = new BrowserProvider(window.ethereum);
      const signer = await this.provider.getSigner();
      const address = await signer.getAddress();
      const network = await this.provider.getNetwork();
      const currentChainId = Number(network.chainId);

      this.logger.log('🔷 Connected to chain:', currentChainId, 'Required:', config.web3.bscChainId);

      // If not on BSC, try to switch automatically
      if (currentChainId !== config.web3.bscChainId) {
        this.logger.log('🔷 Wrong network detected, attempting automatic switch to BSC...');
        try {
          await this.switchToBSC();
          // Re-initialize provider after network switch
          this.provider = new BrowserProvider(window.ethereum);
          this.logger.log('🔷 Successfully switched to BSC');
        } catch (switchError: any) {
          this.logger.log('🔷 Auto-switch failed, user will need to switch manually:', switchError.message);
          // Continue with connection - UI will show switch network button
        }
      }

      // Get updated network info after potential switch
      const updatedNetwork = await this.provider.getNetwork();
      const finalChainId = Number(updatedNetwork.chainId);

      // Get USDT balance - pass chainId directly since state hasn't been updated yet
      const balance = await this.getUsdtBalance(address, finalChainId);

      this.updateConnectionState({
        connected: true,
        address,
        chainId: finalChainId,
        balance,
        walletConnectUri: null
      });

      return this.connectionStateSubject.value;
    } catch (error: any) {
      this.logger.error('Error connecting wallet:', error);

      // Handle user rejection
      if (error.code === 4001) {
        throw new Error('User rejected the connection request');
      }

      throw error;
    }
  }

  /**
   * Connect via WalletConnect (for mobile wallets)
   */
  async connectWalletConnect(): Promise<Web3ConnectionState> {
    try {
      const config = await this.getConfig();
      if (!config) {
        throw new Error('Web3 configuration not loaded');
      }

      this.logger.log('🔷 Starting WalletConnect initialization...');
      this.logger.log('🔷 Project ID:', config.reown.projectId);
      this.logger.log('🔷 Chain ID:', config.web3.bscChainId);

      const bscChainId = config.web3.bscChainId;

      // Initialize WalletConnect provider - ONLY allow BSC network
      // Setting chains to [bscChainId] and optionalChains to empty array
      // forces the wallet to connect on BSC network only
      this.walletConnectProvider = await EthereumProvider.init({
        projectId: config.reown.projectId,
        chains: [bscChainId], // Required chain - BSC only
        optionalChains: [], // No optional chains - BSC only
        showQrModal: false, // We'll show our own QR code
        metadata: {...config.reown.metadata, 
          redirect: {
            // native: 'nam3less://',
            universal: `${window.location.origin}/walletconnect`
          }
        },
        methods: ['eth_sendTransaction', 'personal_sign', 'eth_signTypedData', 'eth_sign'],
        events: ['chainChanged', 'accountsChanged']
      });

      this.logger.log('🔷 WalletConnect provider initialized');
      this.logger.log('🔷 Provider session:', this.walletConnectProvider.session);
      this.logger.log('🔷 Provider connected:', this.walletConnectProvider.connected);

      // Listen for display_uri event to get QR code
      this.walletConnectProvider.on('display_uri', (uri: string) => {
        this.logger.log('🔷 display_uri event fired! URI:', uri);
        this.updateConnectionState({ walletConnectUri: uri });
      });

      // Listen for session events
      this.walletConnectProvider.on('connect', async () => {
        this.logger.log('🔷 WalletConnect connect event fired');
        await this.updateWalletConnectState();
      });

      this.walletConnectProvider.on('disconnect', () => {
        this.logger.log('🔷 WalletConnect disconnect event fired');
        this.handleDisconnect();
      });

      this.walletConnectProvider.on('accountsChanged', (accounts: string[]) => {
        this.logger.log('🔷 WalletConnect accountsChanged event fired');
        if (accounts.length > 0) {
          this.handleAccountChange(accounts[0]);
        }
      });

      // Listen for chain changes in WalletConnect
      this.walletConnectProvider.on('chainChanged', async (chainId: string) => {
        this.logger.log('🔷 WalletConnect chainChanged event fired:', chainId);
        const currentState = this.connectionStateSubject.value;
        if (currentState.connected && currentState.address) {
          // Re-initialize provider with new network
          this.provider = new BrowserProvider(this.walletConnectProvider as any);
          const balance = await this.getUsdtBalance(currentState.address);

          this.updateConnectionState({
            chainId: +chainId,
            balance: balance
          });
        }
      });

      this.logger.log('🔷 Event listeners set up, calling connect()...');

      // Check if there's an existing session and clear it to force QR code display
      if (this.walletConnectProvider.session) {
        this.logger.log('🔷 Found existing session, disconnecting to force new QR code...');
        await this.walletConnectProvider.disconnect();
      }

      // Connect (triggers QR code display via 'display_uri' event)
      // Using connect() instead of enable() to ensure fresh pairing
      await this.walletConnectProvider.connect();

      this.logger.log('🔷 connect() completed');

      // Update state after connection completes
      await this.updateWalletConnectState();

      return this.connectionStateSubject.value;
    } catch (error: any) {
      this.logger.error('🔷 Error in connectWalletConnect:', error);
      throw new Error(`WalletConnect error: ${error.message}`);
    }
  }

  private async updateWalletConnectState(): Promise<void> {
    if (!this.walletConnectProvider) return;

    try {
      const accounts = this.walletConnectProvider.accounts;
      const chainId = this.walletConnectProvider.chainId;

      if (accounts && accounts.length > 0) {
        this.provider = new BrowserProvider(this.walletConnectProvider as any);
        // Pass chainId directly since state hasn't been updated yet
        const balance = await this.getUsdtBalance(accounts[0], chainId);

        this.updateConnectionState({
          connected: true,
          address: accounts[0],
          chainId: chainId,
          balance,
          walletConnectUri: null // Clear URI after connection
        });
      }
    } catch (error) {
      this.logger.error('Error updating WalletConnect state:', error);
    }
  }

  /**
   * Disconnect wallet
   */
  async disconnectWallet(): Promise<void> {
    if (this.walletConnectProvider) {
      await this.walletConnectProvider.disconnect();
      this.walletConnectProvider = null;
    }
    this.handleDisconnect();
  }

  /**
   * Get current connection state
   */
  getConnectionState(): Web3ConnectionState {
    return this.connectionStateSubject.value;
  }

  /**
   * Check if connected to BSC network
   */
  async isConnectedToBSC(): Promise<boolean> {
    const config = await this.getConfig();
    if (!config) {
      return false;
    }
    return this.connectionStateSubject.value.chainId === config.web3.bscChainId;
  }

  /**
   * Switch to BSC network
   */
  async switchToBSC(): Promise<void> {
    const config = await this.getConfig();
    if (!config) {
      throw new Error('Web3 configuration not loaded');
    }

    const bscChainIdHex = `0x${config.web3.bscChainId.toString(16)}`;

    // Try WalletConnect provider first if available
    if (this.walletConnectProvider) {
      try {
        this.logger.log('🔷 Attempting to switch network via WalletConnect');
        await this.walletConnectProvider.request({
          method: 'wallet_switchEthereumChain',
          params: [{ chainId: bscChainIdHex }]
        });
        return;
      } catch (error: any) {
        this.logger.log('🔷 WalletConnect network switch error:', error);
        // If network not added, try to add it
        if (error.code === 4902 || error.message?.includes('Unrecognized chain')) {
          try {
            await this.walletConnectProvider.request({
              method: 'wallet_addEthereumChain',
              params: [{
                chainId: bscChainIdHex,
                chainName: 'BNB Smart Chain',
                nativeCurrency: {
                  name: 'BNB',
                  symbol: 'BNB',
                  decimals: 18
                },
                rpcUrls: ['https://bsc-dataseed.binance.org'],
                blockExplorerUrls: ['https://bscscan.com']
              }]
            });
            return;
          } catch (addError: any) {
            this.logger.error('🔷 Failed to add BSC network:', addError);
            throw new Error('Please switch to BSC network manually in your wallet app');
          }
        }
        throw new Error('Please switch to BSC network manually in your wallet app');
      }
    }

    // Fallback to browser provider
    if (!this.provider) {
      throw new Error('Wallet not connected');
    }

    try {
      // Request network switch
      await this.provider.send('wallet_switchEthereumChain', [
        { chainId: bscChainIdHex }
      ]);
    } catch (error: any) {
      // If network not added, add it
      if (error.code === 4902) {
        await this.provider.send('wallet_addEthereumChain', [
          {
            chainId: bscChainIdHex,
            chainName: 'BNB Smart Chain',
            nativeCurrency: {
              name: 'BNB',
              symbol: 'BNB',
              decimals: 18
            },
            rpcUrls: ['https://bsc-dataseed.binance.org'],
            blockExplorerUrls: ['https://bscscan.com']
          }
        ]);
      } else {
        throw error;
      }
    }
  }

  /**
   * Get USDT balance for an address
   * Returns '0' if not on BSC network (contract address is BSC-specific)
   * @param address - The wallet address to check
   * @param chainIdOverride - Optional chainId to use instead of state (useful during initial connection)
   */
  async getUsdtBalance(address: string, chainIdOverride?: number): Promise<string> {
    return this.getTokenBalance(address, 'USDT', chainIdOverride);
  }

  /**
   * Get the BSC balance of a settlement token (USDT or USDC) for an address.
   * Returns '0' if not on BSC (contract addresses are BSC-specific). Both BSC
   * stablecoins use 18 decimals.
   */
  async getTokenBalance(address: string, token: 'USDT' | 'USDC' = 'USDT', chainIdOverride?: number): Promise<string> {
    if (!this.provider) {
      this.logger.log('🔷 getTokenBalance: Provider not initialized');
      return '0';
    }

    const config = await this.getConfig();
    if (!config) {
      this.logger.log('🔷 getTokenBalance: Config not loaded');
      return '0';
    }

    // Use provided chainId or fall back to state (override needed mid-connect).
    const currentChainId = chainIdOverride ?? this.connectionStateSubject.value.chainId;
    if (currentChainId !== config.web3.bscChainId) {
      this.logger.log(`🔷 getTokenBalance: Wrong network (${currentChainId} != ${config.web3.bscChainId}), returning 0`);
      return '0';
    }

    const contractAddress = token === 'USDC'
      ? (config.web3.usdcContractAddress || '0x8AC76a51cc950d9822D68b83fE1Ad97B32Cd580d')
      : config.web3.usdtContractAddress;

    try {
      const contract = new Contract(contractAddress, USDT_ABI, this.provider);
      const balance = await contract['balanceOf'](address);
      const formattedBalance = formatUnits(balance, 18); // USDT & USDC: 18 decimals on BSC
      this.logger.log(`🔷 getTokenBalance: ${formattedBalance} ${token} for ${address}`);
      return formattedBalance;
    } catch (error) {
      this.logger.error(`Error getting ${token} balance:`, error);
      return '0';
    }
  }

  /**
   * Send a USDT payment (kept for backwards compatibility).
   */
  async sendUsdtPayment(
    recipientAddress: string,
    amountUsdt: number
  ): Promise<string> {
    return this.sendTokenPayment(recipientAddress, amountUsdt, 'USDT');
  }

  /**
   * Send a stablecoin payment (USDT or USDC) on BSC. Both use 18 decimals; the
   * token only selects which ERC-20 contract the transfer runs against.
   */
  async sendTokenPayment(
    recipientAddress: string,
    amount: number,
    token: 'USDT' | 'USDC' = 'USDT'
  ): Promise<string> {
    if (!this.provider) {
      throw new Error('Wallet not connected');
    }

    const config = await this.getConfig();
    if (!config) {
      throw new Error('Web3 configuration not loaded');
    }

    if (!this.isConnectedToBSC()) {
      throw new Error('Please switch to BSC network');
    }

    const contractAddress = token === 'USDC'
      ? (config.web3.usdcContractAddress || '0x8AC76a51cc950d9822D68b83fE1Ad97B32Cd580d')
      : config.web3.usdtContractAddress;

    try {
      const signer = await this.provider.getSigner();
      const tokenContract = new Contract(
        contractAddress,
        USDT_ABI,
        signer
      );

      // Convert amount to wei (18 decimals for USDT & USDC on BSC)
      // Ensure we have a clean decimal string
      const amountStr = Number(amount).toFixed(6); // Limit to 6 decimals for precision
      const amountWei = parseUnits(amountStr, 18);

      this.logger.log(`Sending ${token} payment:`, {
        to: recipientAddress,
        amount: amount,
        amountStr: amountStr,
        amountWei: amountWei.toString()
      });

      // Execute transfer - use bracket notation for contract method
      const tx = await tokenContract['transfer'](recipientAddress, amountWei);

      this.logger.log('Transaction submitted:', tx.hash);

      // Don't wait for confirmation, return immediately
      return tx.hash;
    } catch (error: any) {
      this.logger.error(`Error sending ${token} payment:`, error);

      // Handle user rejection
      if (error.code === 'ACTION_REJECTED' || error.code === 4001) {
        throw new Error('Transaction rejected by user');
      }

      // Handle insufficient funds
      if (error.code === 'INSUFFICIENT_FUNDS') {
        throw new Error('Insufficient BNB for gas fees');
      }

      throw error;
    }
  }

  /**
   * Check if user has sufficient USDT balance
   */
  async hasSufficientBalance(requiredAmount: number): Promise<boolean> {
    const state = this.connectionStateSubject.value;
    if (!state.address || !state.chainId) {
      return false;
    }

    // Pass chainId from state for consistency
    const balance = await this.getUsdtBalance(state.address, state.chainId);
    return parseFloat(balance) >= requiredAmount;
  }

  /**
   * Get transaction receipt (for debugging)
   */
  async getTransactionReceipt(txHash: string): Promise<any> {
    if (!this.provider) {
      throw new Error('Provider not initialized');
    }

    return await this.provider.getTransactionReceipt(txHash);
  }

  /**
   * Get current block number
   */
  async getBlockNumber(): Promise<number> {
    if (!this.provider) {
      throw new Error('Provider not initialized');
    }

    return await this.provider.getBlockNumber();
  }
}
